import { type FC, useEffect, useRef, useState } from "react";
import { FlatList, Platform, Pressable, View } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import "../../../theme";
import { Icon } from "../../Icon";
import { Text } from "../../Text";
import { SearchEmpty } from "../../SearchInput/parts/SearchEmpty";
import { type CountryOption, getFlagEmoji } from "../phone-number";

interface CountryListProps {
  readonly options: readonly CountryOption[];
  readonly selected: CountryOption["country"];
  /** Keyboard-highlighted row (web); -1 for none. */
  readonly highlightedIndex: number;
  readonly onSelect: (option: CountryOption) => void;
  readonly onHighlight: (index: number) => void;
}

/**
 * Virtualised so the ~245 rows stay cheap on native. Shared by the web
 * dropdown and the native sheet so both list countries identically.
 */
const CountryList: FC<CountryListProps> = ({ options, selected, highlightedIndex, onSelect, onHighlight }) => {
  if (options.length === 0) return <SearchEmpty message="No matching countries" />;

  return (
    <FlatList
      data={options}
      keyExtractor={(o) => o.country}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode="on-drag"
      // Web renders every row so keyboard highlighting can scroll any of them
      // into view; virtualisation only pays off on native.
      {...(Platform.OS === "web" ? { initialNumToRender: options.length } : {})}
      renderItem={({ item, index }) => (
        <CountryRow
          option={item}
          selected={item.country === selected}
          highlighted={index === highlightedIndex}
          onPress={() => onSelect(item)}
          onHoverIn={() => onHighlight(index)}
        />
      )}
    />
  );
};

CountryList.displayName = "CountryList";

interface CountryRowProps {
  readonly option: CountryOption;
  readonly selected: boolean;
  readonly highlighted: boolean;
  readonly onPress: () => void;
  readonly onHoverIn: () => void;
}

const CountryRow: FC<CountryRowProps> = ({ option, selected, highlighted, onPress, onHoverIn }) => {
  const { theme } = useUnistyles();
  const [hovered, setHovered] = useState(false);
  const ref = useRef<View>(null);

  useEffect(() => {
    if (highlighted && Platform.OS === "web" && ref.current) {
      (ref.current as unknown as HTMLElement).scrollIntoView({ block: "nearest" });
    }
  }, [highlighted]);

  return (
    <Pressable
      ref={ref}
      onPress={onPress}
      onHoverIn={() => {
        setHovered(true);
        onHoverIn();
      }}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={`${option.name} +${option.callingCode}`}
      accessibilityState={{ selected }}
      style={({ pressed }) => [styles.row, (highlighted || hovered || pressed) && styles.rowHighlighted]}
    >
      <Text textStyle="md/regular">{getFlagEmoji(option.country)}</Text>
      <Text textStyle={selected ? "sm/medium" : "sm/regular"} color="text" numberOfLines={1} style={styles.name}>
        {option.name}
      </Text>
      <Text textStyle="sm/regular" color="textMuted">
        +{option.callingCode}
      </Text>
      <View style={styles.check}>{selected && <Icon name="checkmark" size={16} color={theme.colors.text} />}</View>
    </Pressable>
  );
};

CountryRow.displayName = "CountryRow";

export { CountryList };

const styles = StyleSheet.create((theme) => ({
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.lg,
    // 48pt keeps rows comfortably tappable on phones.
    minHeight: 48,
    paddingHorizontal: theme.spacing["3xl"],
    userSelect: "none",
  },
  rowHighlighted: {
    backgroundColor: theme.colors.surface2.hover,
  },
  name: {
    flex: 1,
    minWidth: 0,
  },
  check: {
    width: 16,
  },
}));
